import { useTranslation } from 'react-i18next';
import { cn } from '@c2fo/liquidity';
import Skeleton from '@/enterprise/components/Skeleton';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useMarketStatsStatus } from '@/lib/useMarketStatsStatus';
import Divider from '@/shared/components/Divider';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useLocaleFormat from '@/utils/useLocaleFormat';

interface TakerMarketStatsGridProps {
  takerId: number;
  takerMarket: TakerMarket;
}

const TakerMarketStatsGridComponent = ({ takerId, takerMarket }: TakerMarketStatsGridProps) => {
  const { t } = useTranslation();
  const { asCurrency, asNumber, asPercent } = useLocaleFormat();
  const { listenToMarketStats } = useMarketStatsStatus();
  const listeningForMarketStat = listenToMarketStats([
    {
      marketUuid: takerMarket.marketUuid,
      takerId,
      takerDivisionUuid: takerMarket.takerDivisionUuid,
      makerOrganizationUuid: takerMarket.makerOrganizationUuid,
    },
  ]);

  const showDiscount = takerMarket.marketPricingType !== 'BENCHMARK';
  const showApr = takerMarket.offerConfig.isDiscountBidding ?? false;
  const showDivider = showDiscount && showApr;

  const eligibleInvoiceAmount = takerMarket.eligibleInvoiceAmount + takerMarket.awardedInvoiceAmount;
  const eligibleDpeWeightedSum = takerMarket.eligibleDpeWeightedSum + takerMarket.awardedDpeWeightedSum;

  const acceptedInvoiceAmount = takerMarket.clearingInvoiceAmount;
  const estimatedAmount = acceptedInvoiceAmount - takerMarket.clearingEarn;

  const dpeWeightedAvg = takerMarket.clearingDpeWeightedAvg;
  const earn = takerMarket.clearingEarn;
  const discountWeightedAvg = takerMarket.clearingDiscountWeightedAvg;
  const aprWeightedAvg = takerMarket.clearingAprWeightedAvg;

  return (
    <div className="rounded-2xl bg-canvas">
      <div className="grid grid-cols-1 border-none p-6 sm:grid-cols-2 lg:grid-cols-4 lg:[&>*:nth-child(2)]:border-r lg:[&>*:nth-child(2)]:border-deepGray-500/[12%] sm:[&>*:nth-child(odd)]:border-r sm:[&>*:nth-child(odd)]:border-deepGray-500/[12%]">
        <div className="mb-6 text-center sm:mb-3 lg:mb-0">
          <div className="text-sm text-text-secondary">{t('core.totalAmount')}</div>
          <div className={cn('text-xl font-medium', { 'blur-sm': listeningForMarketStat })}>
            {asCurrency(takerMarket.totalInvoiceAmount, takerMarket.currency)}
          </div>
          <div className={cn('text-sm text-text-secondary', { 'blur-sm': listeningForMarketStat })}>
            {t('core.invoicesWithCount', {
              count: takerMarket.totalInvoiceCount || 0,
              formattedCount: asNumber({ value: takerMarket.totalInvoiceCount || 0 }),
            })}
          </div>
        </div>
        <div className="mb-6 text-center sm:mb-3 lg:mb-0">
          <div className="text-sm text-text-secondary">{t('core.includedAmount')}</div>
          <div className={cn('text-xl font-medium', { 'blur-sm': listeningForMarketStat })}>
            {asCurrency(eligibleInvoiceAmount, takerMarket.currency)}
          </div>
          <div className={cn('text-sm text-text-secondary', { 'blur-sm': listeningForMarketStat })}>
            {t('taker.marketSummaryAverageDPE', {
              dpe: asNumber({
                value: eligibleInvoiceAmount > 0 ? eligibleDpeWeightedSum / eligibleInvoiceAmount : 0,
                decimals: 0,
              }),
            })}
          </div>
        </div>
        <div className="relative mb-3 flex items-center justify-center lg:mb-0">
          <div className="text-center">
            <div className="text-sm text-text-secondary">{t('core.estimatedAcceptanceAmount')}</div>
            <div
              className={cn('text-xl font-medium', {
                'blur-sm': listeningForMarketStat,
              })}
            >
              {takerMarket.marketIsEnabled ? asCurrency(acceptedInvoiceAmount, takerMarket.currency) : t('core.na')}
            </div>
            <div
              className={cn('text-sm text-text-secondary', {
                invisible: !takerMarket.marketIsEnabled || false,
                'blur-sm': listeningForMarketStat,
              })}
            >
              {t('taker.marketSummaryAverageDPE', {
                dpe: asNumber({ value: dpeWeightedAvg || 0, decimals: 0 }),
              })}
            </div>
          </div>
        </div>
        <div className="mt-6 text-center sm:mt-3 lg:mt-0">
          <div className="text-sm text-text-secondary">{t('core.estimatedEarlyPaymentAmount')}</div>
          <div className={cn('text-xl font-medium', { 'blur-sm': listeningForMarketStat })}>
            {takerMarket.marketIsEnabled ? asCurrency(estimatedAmount, takerMarket.currency) : t('core.na')}
          </div>
          <div
            className={cn('flex items-center justify-center space-x-2 text-sm uppercase text-text-secondary', {
              invisible: !takerMarket.marketIsEnabled || false,
              'blur-sm': listeningForMarketStat,
            })}
          >
            {takerMarket.offerConfig.isEnabled ? (
              <>
                <div>
                  {takerMarket.marketIsEnabled
                    ? `${earn > 0 ? '-' : ''}${asCurrency(earn, takerMarket.currency)}`
                    : t('core.na')}
                </div>
                <Divider vertical />
              </>
            ) : null}
            {showDiscount && (
              <div>
                {`${asPercent({ value: discountWeightedAvg / 100 || 0, decimals: 2 })} ${t(
                  'core.discountAbbreviation'
                )}`}
              </div>
            )}
            {showDivider && <Divider vertical />}
            {showApr && (
              <div>
                {`${asPercent({ value: aprWeightedAvg / 100 || 0, decimals: 2 })} ${t('core.aprAbbreviation')}`}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TakerMarketStatsGrid = (props: TakerMarketStatsGridProps) => (
  <QueryBoundaries LoadingComponent={() => <Skeleton className="h-28" />}>
    <TakerMarketStatsGridComponent {...props} />
  </QueryBoundaries>
);

export default TakerMarketStatsGrid;
